import { useTranslation } from 'react-i18next';
import { useServicePropertyPreview } from '../../hooks/useServicePropertyPreview';
import { parseTablePreviewRows } from '../../data/tablePreviewParse';
import { ReferenceSourcedPreview } from './ReferenceSourcedPreview';
import { TemplateContentPreview } from './TemplateContentPreview';

interface ServiceSourcedPreviewProps {
  templateId: string | undefined;
  tokenName: string;
  propertyType: string;
  configJson: string;
  displayName: string;
  values: Record<string, string>;
  /** The property's own Default value, already resolved to a reference key when it names one
   * (e.g. `{{@CoverImages}}` -> "CoverImages") — shown immediately as a fallback, before the
   * live call resolves, and again if the live call fails or returns nothing. */
  resolvedDefault: string;
  /** Only meaningful when the fallback names an imageCollection entry. */
  collectionItem?: string;
}

/** A `source: service` Image/Table/DocumentDictionary — actually calls the configured service,
 * live, once every mapped input value is filled in. See useServicePropertyPreview. */
export function ServiceSourcedPreview({
  templateId,
  tokenName,
  propertyType,
  configJson,
  displayName,
  values,
  resolvedDefault,
  collectionItem
}: ServiceSourcedPreviewProps) {
  const { t } = useTranslation();
  const { status, result } = useServicePropertyPreview(templateId, tokenName, configJson, values);

  if (status === 'ready' && result?.success) {
    if (propertyType === 'Image') {
      return <TemplateContentPreview image={{ src: result.preview, alt: displayName }} />;
    }

    if (propertyType === 'Table') {
      return <TemplateContentPreview table={parseTablePreviewRows(result.preview)} />;
    }

    return (
      <TemplateContentPreview
        document={{ fileName: '', description: t('wizard.form.previewGeneratedDocument'), downloadHref: result.preview }}
      />
    );
  }

  // The service hasn't returned anything usable yet (or failed) — the Default value's own
  // fallback is shown in its place, exactly as it would if the service were never configured.
  if (templateId && resolvedDefault.trim() !== '') {
    return (
      <ReferenceSourcedPreview
        templateId={templateId}
        propertyType={propertyType}
        displayName={displayName}
        entryKey={resolvedDefault}
        collectionItem={propertyType === 'Image' ? collectionItem : undefined}
      />
    );
  }

  if (status === 'idle') {
    return <p className="mt-1 text-sm text-muted-foreground">{t('wizard.form.notConfigured')}</p>;
  }

  if (status === 'waiting') {
    return <p className="mt-1 text-sm text-muted-foreground">{t('wizard.form.previewWaiting')}</p>;
  }

  if (status === 'pending') {
    return <p className="mt-1 text-sm text-muted-foreground">{t('wizard.form.previewGenerating')}</p>;
  }

  if (status === 'error' || !result) {
    return <p className="mt-1 text-sm text-destructive">{t('wizard.form.previewFailed')}</p>;
  }

  return <p className="mt-1 text-sm text-destructive">{result.error || t('wizard.form.previewFailed')}</p>;
}
